import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Slider,
  Chip,
  Autocomplete,
  OutlinedInput,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { clientFormSchema } from '@/utils/validationSchemas';
import {
  ClientFormData,
  ClientSource,
  ClientStatus,
  PropertyType,
  ListingType,
  Client,
} from '@/types';

interface ClientFormProps {
  client?: Client;
  onSubmit: (data: ClientFormData, avatarFile?: File) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  agents?: { id: string; name: string }[]; // For agent assignment if needed
}

const steps = [
  'Basic Information',
  'Address',
  'Preferences',
  'Features & Timeline',
  'Matching Profile',
  'Review',
];

const PROPERTY_TYPES = Object.values(PropertyType);
const LISTING_TYPES = Object.values(ListingType);
const CLIENT_SOURCES = Object.values(ClientSource);
const CLIENT_STATUSES = Object.values(ClientStatus);

const DRAFT_KEY = 'client_form_draft';

const ClientForm: React.FC<ClientFormProps> = ({
  client,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [draftRestored, setDraftRestored] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(client?.profile.avatar || null);
  const [avatarFile, setAvatarFile] = useState<File | undefined>(undefined);

  const getInitialValues = (): ClientFormData => {
    if (client) {
      return {
        profile: client.profile,
        preferences: client.preferences,
        matchingProfile: {
          behavior: client.matchingProfile.behavior,
          communication: client.matchingProfile.communication,
          priority: client.matchingProfile.priority,
        },
        source: client.source,
        status: client.status,
        tags: client.tags,
        notes: client.notes,
      };
    }

    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        return JSON.parse(draft);
      }
    } catch (e) {
      console.error('Failed to parse client draft', e);
    }

    return {
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'USA',
        },
      },
      preferences: {
        priceRange: { min: 0, max: 1000000, currency: 'USD' },
        propertyTypes: [],
        listingTypes: [],
        location: {
          cities: [],
          neighborhoods: [],
        },
        features: {
          bedrooms: { min: 1, max: 5 },
          bathrooms: { min: 1, max: 3 },
          squareFootage: { min: 500, max: 5000 },
          amenities: [],
          mustHaves: [],
          dealBreakers: [],
        },
        timeline: {
          urgency: 'medium',
          flexibility: 'medium',
        },
        financing: {
          preApproved: false,
          downPayment: 0,
        },
      },
      matchingProfile: {
        behavior: {
          searchFrequency: 'weekly',
          responseTime: 'within_day',
          engagementLevel: 'medium',
          preferredContactMethod: 'email',
        },
        communication: {
          communicationStyle: 'mixed',
          language: 'en',
          timezone: 'UTC',
          preferredTimes: ['morning', 'afternoon'],
        },
        priority: {
          price: 5,
          location: 5,
          features: 5,
          timeline: 5,
          quality: 5,
        },
      },
      source: ClientSource.WEBSITE,
      status: ClientStatus.DISCOVERY,
      tags: [],
      notes: '',
    };
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setValue,
  } = useForm<ClientFormData>({
    resolver: yupResolver(clientFormSchema),
    defaultValues: getInitialValues(),
  });

  const formValues = watch();

  React.useEffect(() => {
    if (!client) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [formValues, client]);

  React.useEffect(() => {
    if (!client && localStorage.getItem(DRAFT_KEY)) {
      setDraftRestored(true);
    }
  }, [client]);

  const handleClearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftRestored(false);
    window.location.reload();
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(undefined);
    setAvatarPreview(null);
    setValue('profile.avatar', undefined);
  };

  const handleNext = async () => {
    const isValid = await handleStepValidation();
    if (isValid) {
      setActiveStep((prev) => prev + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleStepValidation = async (): Promise<boolean> => {
    try {
      let fieldsToValidate: any[] = [];

      switch (activeStep) {
        case 0:
          fieldsToValidate = ['profile', 'source', 'status'];
          break;
        case 1:
          fieldsToValidate = ['profile.address'];
          break;
        case 2:
          fieldsToValidate = ['preferences.priceRange', 'preferences.propertyTypes', 'preferences.listingTypes', 'preferences.location'];
          break;
        case 3:
          fieldsToValidate = ['preferences.features', 'preferences.timeline', 'preferences.financing'];
          break;
        case 4:
          fieldsToValidate = ['matchingProfile'];
          break;
        case 5:
          return true;
      }

      const result = await trigger(fieldsToValidate);
      return result;
    } catch (error: any) {
      setError(error.message || 'Please fill in all required fields');
      return false;
    }
  };

  const onFormSubmit = async (data: ClientFormData) => {
    try {
      setError(null);
      await onSubmit(data, avatarFile);
      if (!client) {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save client');
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Stack direction="column" alignItems="center" spacing={2}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={avatarPreview || undefined}
                    sx={{ width: 100, height: 100 }}
                  />
                  {avatarPreview && (
                    <IconButton
                      size="small"
                      onClick={handleRemoveAvatar}
                      sx={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleAvatarUpload}
                />
                <label htmlFor="avatar-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    size="small"
                  >
                    Upload Avatar
                  </Button>
                </label>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    required
                    error={!!errors.profile?.firstName}
                    helperText={errors.profile?.firstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    required
                    error={!!errors.profile?.lastName}
                    helperText={errors.profile?.lastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    required
                    error={!!errors.profile?.email}
                    helperText={errors.profile?.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    fullWidth
                    required
                    error={!!errors.profile?.phone}
                    helperText={errors.profile?.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="source"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Source</InputLabel>
                    <Select {...field} label="Source">
                      {CLIENT_SOURCES.map((source) => (
                        <MenuItem key={source} value={source}>
                          {source.replace(/_/g, ' ').toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      {CLIENT_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.replace(/_/g, ' ').toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="profile.address.street"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Street Address"
                    fullWidth
                    error={!!errors.profile?.address?.street}
                    helperText={errors.profile?.address?.street?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.address.city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    fullWidth
                    error={!!errors.profile?.address?.city}
                    helperText={errors.profile?.address?.city?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.address.state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="State"
                    fullWidth
                    error={!!errors.profile?.address?.state}
                    helperText={errors.profile?.address?.state?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.address.zipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Zip Code"
                    fullWidth
                    error={!!errors.profile?.address?.zipCode}
                    helperText={errors.profile?.address?.zipCode?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="profile.address.country"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Country"
                    fullWidth
                    error={!!errors.profile?.address?.country}
                    helperText={errors.profile?.address?.country?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Budget Range</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="preferences.priceRange.min"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Min Budget"
                        fullWidth
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="preferences.priceRange.max"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Max Budget"
                        fullWidth
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="preferences.priceRange.currency"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          <MenuItem value="USD">USD</MenuItem>
                          <MenuItem value="EUR">EUR</MenuItem>
                          <MenuItem value="GBP">GBP</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Location Preferences</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="preferences.location.cities"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={value || []}
                        onChange={(_, newValue) => onChange(newValue)}
                        renderTags={(value: readonly string[], getTagProps) =>
                          value.map((option: string, index: number) => (
                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Preferred Cities"
                            placeholder="Type and press enter to add cities"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="preferences.location.neighborhoods"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={value || []}
                        onChange={(_, newValue) => onChange(newValue)}
                        renderTags={(value: readonly string[], getTagProps) =>
                          value.map((option: string, index: number) => (
                            <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Preferred Neighborhoods"
                            placeholder="Type and press enter to add neighborhoods"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="preferences.propertyTypes"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Property Types</InputLabel>
                    <Select
                      {...field}
                      multiple
                      input={<OutlinedInput label="Property Types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {PROPERTY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="preferences.listingTypes"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Listing Types</InputLabel>
                    <Select
                      {...field}
                      multiple
                      input={<OutlinedInput label="Listing Types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {LISTING_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Bedrooms</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="preferences.features.bedrooms.min"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} type="number" label="Min Bedrooms" fullWidth onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="preferences.features.bedrooms.max"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} type="number" label="Max Bedrooms" fullWidth onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Bathrooms</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="preferences.features.bathrooms.min"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} type="number" label="Min Bathrooms" fullWidth onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="preferences.features.bathrooms.max"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} type="number" label="Max Bathrooms" fullWidth onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferences.timeline.urgency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Urgency</InputLabel>
                    <Select {...field} label="Urgency">
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferences.timeline.flexibility"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Flexibility</InputLabel>
                    <Select {...field} label="Flexibility">
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="preferences.financing.preApproved"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Pre-Approved for Mortgage"
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Priority Settings (0-10)</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Set the importance of different factors for matching algorithm
              </Typography>
            </Grid>
            
            {['price', 'location', 'features', 'timeline', 'quality'].map((priority) => (
              <Grid item xs={12} sm={6} key={priority}>
                <Typography gutterBottom>
                  {priority.charAt(0).toUpperCase() + priority.slice(1)}
                </Typography>
                <Controller
                  name={`matchingProfile.priority.${priority}` as any}
                  control={control}
                  render={({ field }) => (
                    <Slider
                      {...field}
                      min={0}
                      max={10}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                      onChange={(_, value) => field.onChange(value)}
                    />
                  )}
                />
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              <Controller
                name="matchingProfile.behavior.searchFrequency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Search Frequency</InputLabel>
                    <Select {...field} label="Search Frequency">
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="matchingProfile.behavior.preferredContactMethod"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Preferred Contact</InputLabel>
                    <Select {...field} label="Preferred Contact">
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="app">App</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review Client Information</Typography>
            <Paper sx={{ p: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {avatarPreview && (
                  <Avatar src={avatarPreview} sx={{ width: 60, height: 60, mr: 2 }} />
                )}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Profile</Typography>
                  <Typography>Name: {watch('profile.firstName')} {watch('profile.lastName')}</Typography>
                </Box>
              </Box>
              <Typography>Email: {watch('profile.email')}</Typography>
              <Typography>Phone: {watch('profile.phone')}</Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>Preferences</Typography>
              <Typography>
                Budget: {watch('preferences.priceRange.currency')} {watch('preferences.priceRange.min').toLocaleString()} - {watch('preferences.priceRange.max').toLocaleString()}
              </Typography>
              <Typography>
                Property Types: {watch('preferences.propertyTypes')?.join(', ') || 'Any'}
              </Typography>
              <Typography>
                Bedrooms: {watch('preferences.features.bedrooms.min')} - {watch('preferences.features.bedrooms.max')}
              </Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>Status</Typography>
              <Chip label={watch('status')} color="primary" size="small" />
              <Chip label={watch('source')} sx={{ ml: 1 }} size="small" />
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {draftRestored && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearDraft}>
              Clear Draft
            </Button>
          }
        >
          A saved draft was restored. You can continue editing or clear it to start fresh.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Paper sx={{ p: 3, mb: 3 }}>
          {renderStepContent(activeStep)}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <>
                {onCancel && (
                  <Button onClick={onCancel} sx={{ mr: 2 }}>
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Saving...' : client ? 'Update Client' : 'Create Client'}
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default ClientForm;
